import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Footer from "./component/Footer";
import AccountSidebar from "./component/AccountSidebar";
import api from "./api";
import Swal from "sweetalert2";

const MyOrder = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("ongoing");
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const [mobileView, setMobileView] = useState("menu");

    const storedData = localStorage.getItem("userData");
    const token = storedData ? JSON.parse(storedData).token : null;

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const fn = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener('scroll', fn);
        return () => window.removeEventListener('scroll', fn);
    }, []);

    // ✅ Single-click mobile fix
    useEffect(() => {
        if (sessionStorage.getItem("mob_nav_direct") === "true") {
            sessionStorage.removeItem("mob_nav_direct");
            setMobileView("content");
        }
    }, []);

    const getOrderApi = async () => {
        try {
            setLoading(true);
            const endpoint = activeTab === "history"
                ? "/user/user-orders/history"
                : "/user/user-orders/ongoing";
            const res = await api.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOrder(Array.isArray(res.data.orders) ? res.data.orders : []);
        } catch (e) {
            console.error(e);
            setOrder([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) getOrderApi();
    }, [activeTab, token]);

    //  full Order Cancel
    const handleCancelOrder = async (orderId, e) => {
        if (e) e.stopPropagation();

        const { value: formValues, isConfirmed } = await Swal.fire({
            title: "Cancel Entire Order?",
            html: `
            <p style="color:#666; margin-bottom:14px;">Are you sure you want to cancel this complete order?</p>
            <select id="swal-reason" class="swal2-select" style="width:88%; margin-bottom:10px;">
                <option value="">-- Select a reason --</option>
                <option value="Ordered by mistake">Ordered by mistake</option>
                <option value="Found a better price">Found a better price</option>
                <option value="Delivery taking too long">Delivery taking too long</option>
                <option value="Changed my mind">Changed my mind</option>
                <option value="Other">Other</option>
            </select>
            <textarea id="swal-detail" class="swal2-textarea" placeholder="Additional details (optional)" style="width:88%; resize:none;" rows="3"></textarea>
        `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#c0392b",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Cancel Order",
            cancelButtonText: "Go Back",
            preConfirm: () => {
                const reason = document.getElementById("swal-reason").value;
                const detail = document.getElementById("swal-detail").value.trim();
                if (!reason) {
                    Swal.showValidationMessage("Please select a cancellation reason.");
                    return false;
                }
                return { cancelReason: detail ? `${reason} - ${detail}` : reason };
            },
        });

        if (isConfirmed && formValues) {
            try {
                await api.post(
                    `/user/order/${orderId}/cancel`,
                    { cancelReason: formValues.cancelReason },           // ✅ reason in body
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                Swal.fire({
                    title: "Order Cancelled!",
                    text: "Your order has been cancelled successfully.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
                getOrderApi();
            } catch (err) {
                Swal.fire({
                    title: "Error!",
                    text: err?.response?.data?.message || "Failed to cancel order.",
                    icon: "error",
                    timer: 2500,
                    showConfirmButton: false,
                });
            }
        }
    };

    //  Single Item Cancel Api 
    const handleCancelItem = async (orderId, itemId, productName, e) => {
        if (e) e.stopPropagation();

        const { value: formValues, isConfirmed } = await Swal.fire({
            title: "Cancel Item?",
            html: `
            <p style="color:#666; margin-bottom:14px;">You are cancelling: <strong>${productName}</strong></p>
            <select id="swal-reason" class="swal2-select" style="width:88%; margin-bottom:10px;">
                <option value="">-- Select a reason --</option>
                <option value="Ordered by mistake">Ordered by mistake</option>
                <option value="Found a better price">Found a better price</option>
                <option value="Delivery taking too long">Delivery taking too long</option>
                <option value="Changed my mind">Changed my mind</option>
                <option value="Other">Other</option>
            </select>
            <textarea id="swal-detail" class="swal2-textarea" placeholder="Additional details (optional)" style="width:88%; resize:none;" rows="3"></textarea>
        `,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#c0392b",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, Cancel Item",
            cancelButtonText: "Go Back",
            preConfirm: () => {
                const reason = document.getElementById("swal-reason").value;
                const detail = document.getElementById("swal-detail").value.trim();
                if (!reason) {
                    Swal.showValidationMessage("Please select a cancellation reason.");
                    return false;
                }
                return { cancelReason: detail ? `${reason} - ${detail}` : reason };
            },
        });

        if (isConfirmed && formValues) {
            try {
                await api.get(`/user/cancel-order-item/${orderId}/${itemId}`, {
                    params: { cancelReason: formValues.cancelReason },   // ✅ sent as ?cancelReason=...
                    headers: { Authorization: `Bearer ${token}` },
                });
                Swal.fire({
                    title: "Item Cancelled!",
                    text: `${productName} has been cancelled successfully.`,
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
                getOrderApi();
            } catch (err) {
                Swal.fire({
                    title: "Error!",
                    text: err?.response?.data?.message || "Failed to cancel item.",
                    icon: "error",
                    timer: 2500,
                    showConfirmButton: false,
                });
            }
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'delivered': return { bg: '#e6f9f0', color: '#1a7a4a' };
            case 'cancelled': return { bg: '#fdecea', color: '#c0392b' };
            case 'pending': return { bg: '#fff8e1', color: '#b8860b' };
            case 'processing': return { bg: '#e8f4fd', color: '#1565c0' };
            default: return { bg: '#f0f0f0', color: '#555' };
        }
    };

    const filteredOrders = Array.isArray(order) ? order.filter(o =>
        activeTab === "ongoing"
            ? o.orderStatus !== "delivered" && o.orderStatus?.toLowerCase() !== "cancelled"
            : o.orderStatus === "Delivered" || o.orderStatus?.toLowerCase() === "cancelled"
    ) : [];

    console.log("Order Data >>", filteredOrders)

    const OrderCard = ({ o }) => {
        const s = getStatusColor(o.orderStatus);

        const isOrderCancellable =
            activeTab === "ongoing" &&
            o.orderStatus?.toLowerCase() !== "cancelled" &&
            o.orderStatus?.toLowerCase() !== "delivered";

        return (
            <div className="order-card">

                {/* ── Order Header ── */}
                <div className="order-card-header">
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <p className="order-id">
                            Order ID: <span>{o.orderId}</span>
                        </p>
                        <p className="order-meta">
                            Placed On: {o?.statusHistory?.[0]?.date || "N/A"}
                        </p>
                        <p className="order-meta">
                            Payment: <strong>{o.paymentMethod}</strong>
                        </p>
                    </div>
                    <span className="status-badge" style={{ background: s.bg, color: s.color }}>
                        {o.orderStatus}
                    </span>
                </div>

                {/* ── Order Items — har item ka alag cancel button ── */}
                {Array.isArray(o.orderItems) && o.orderItems.map(item => {
                    const itemCancelled = item?.cancelStatus === "Cancelled";

                    return (
                        <div
                            key={item._id}
                            className={`order-item-row ${itemCancelled ? 'item-cancelled' : ''}`}
                        >
                            {/* Product Image */}
                            {item.variant?.images?.length > 0 && (
                                <img
                                    src={item.variant.images[0]}
                                    alt={item.name}
                                    className="order-item-img"
                                    style={{ opacity: itemCancelled ? 0.4 : 1 }}
                                />
                            )}

                            {/* Product Info */}
                            <div className="order-item-info">
                                <p className="order-item-name">
                                    {item.name || "Product"}
                                </p>
                                <p className="order-item-qty">Qty: {item.quantity}</p>
                                <p className="order-item-price">
                                    ₹{item.price?.discountedPrice ?? item.price?.originalPrice ?? item.price}
                                </p>

                                {/* ✅ Item cancelled badge */}
                                {itemCancelled && (
                                    <span className="item-cancelled-badge">Cancelled</span>
                                )}
                            </div>

                            {/* ✅ Per Item Cancel Button — sirf tab jab item cancel nahi hua ho */}
                            {isOrderCancellable && !itemCancelled && (
                                <button
                                    className="cancel-item-btn"
                                    onClick={(e) =>
                                        handleCancelItem(o._id, item._id, item.name, e)
                                    }
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                    );
                })}

                {/* ── Order Footer ── */}
                <div className="order-card-footer">
                    <p className="order-total">
                        Total: <strong>₹{o.totalAmount}</strong>
                    </p>

                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {/* View Details */}
                        <button
                            className="view-btn"
                            onClick={() =>
                                navigate(`/order-placed/${o._id}`, { state: { orders: o } })
                            }
                        >
                            View Details
                        </button>

                        {/* ✅ Poora Order Cancel Button */}
                        {isOrderCancellable && (
                            <button
                                className="cancel-order-btn"
                                onClick={(e) => handleCancelOrder(o._id, e)}
                            >
                                Cancel Entire Order
                            </button>
                        )}
                    </div>
                </div>

            </div>
        );
    };

    const OrderContent = () => (
        <>
            <div className="tab-bar">
                <button
                    className={`tab-btn ${activeTab === 'ongoing' ? 'active' : ''}`}
                    onClick={() => setActiveTab("ongoing")}
                >
                    Ongoing
                </button>
                <button
                    className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
                    onClick={() => setActiveTab("history")}
                >
                    History
                </button>
            </div>

            {loading ? (
                <div className="loader-wrap">
                    <div className="spinner-border text-primary" role="status" />
                    <p style={{ marginTop: '12px' }}>Loading orders...</p>
                </div>
            ) : filteredOrders.length > 0 ? (
                filteredOrders.map(o => <OrderCard key={o._id} o={o} />)
            ) : (
                <div className="empty-state">
                    <div className="empty-state-icon">📦</div>
                    <p>No {activeTab === 'ongoing' ? 'ongoing' : 'past'} orders found.</p>
                </div>
            )}
        </>
    );

    return (
        <>
            <style>{`
                .dashboard-wrapper { display:flex; min-height:80vh; padding:20px; gap:20px; }
                .dashboard-content { flex:1; min-width:0; padding:10px; }
                .acct-mobile-only-wrapper { display: none; }

                /* Mobile Header */
                .wl-mob-header {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 13px 16px; background: #fff; border-bottom: 1px solid #f0f0f0;
                    position: sticky; top: 0; z-index: 10;
                }
                .wl-mob-back-btn {
                    width: 36px; height: 36px; border: none; background: none;
                    font-size: 28px; color: #333; cursor: pointer;
                }
                .wl-mob-title { font-size: 16px; font-weight: 700; color: #111; margin: 0; }
                .wl-mob-body { padding: 16px; background: #fff; min-height: auto; }

                /* Tabs */
                .tab-bar { display:flex; gap:10px; margin-bottom:20px; }
                .tab-btn {
                    padding:9px 28px; border:none; border-radius:6px;
                    font-size:14px; font-weight:500; cursor:pointer;
                    transition:all 0.2s; background:#e0e0e0; color:#555;
                }
                .tab-btn.active {
                    background:#c9a227; color:#fff;
                    box-shadow:0 2px 8px rgba(201,162,39,0.3);
                }

                /* Order Card */
                .order-card {
                    background:#fff; border-radius:10px;
                    box-shadow:0 2px 10px rgba(0,0,0,0.07);
                    padding:18px; margin-bottom:18px;
                    border:1px solid #f0f0f0;
                }
                .order-card-header {
                    display:flex; justify-content:space-between;
                    align-items:flex-start; flex-wrap:wrap; gap:10px;
                    padding-bottom:12px; border-bottom:1px solid #f0f0f0;
                    margin-bottom:12px;
                }
                .order-id { font-size:13px; font-weight:600; color:#333; margin:0 0 4px; word-break:break-all; }
                .order-id span { color:#c9a227; }
                .order-meta { font-size:13px; color:#666; margin:2px 0; }
                .status-badge {
                    padding:4px 12px; border-radius:20px; font-size:12px;
                    font-weight:600; text-transform:capitalize; white-space:nowrap;
                }

                /* Order Item Row */
                .order-item-row {
                    display:flex; align-items:center; gap:14px;
                    padding:10px; background:#fafafa;
                    border-radius:8px; margin-bottom:8px;
                    transition: all 0.2s;
                }
                .order-item-row.item-cancelled {
                    background:#fff5f5;
                    border: 1px dashed #f5c6c6;
                }
                .order-item-img {
                    width:64px; height:64px; object-fit:cover;
                    border-radius:6px; flex-shrink:0;
                }
                .order-item-info { flex:1; min-width:0; }
                .order-item-name {
                    font-size:14px; font-weight:500; color:#333;
                    margin:0 0 3px; white-space:nowrap;
                    overflow:hidden; text-overflow:ellipsis;
                }
                .order-item-qty { font-size:13px; color:#888; margin:0 0 2px; }
                .order-item-price { font-size:13px; color:#555; font-weight:500; margin:0; }

                /* Item Cancelled Badge */
                .item-cancelled-badge {
                    display:inline-block; margin-top:4px;
                    padding:2px 10px; background:#fdecea;
                    color:#c0392b; border-radius:20px;
                    font-size:11px; font-weight:600;
                }

                /* ✅ Per Item Cancel Button */
                .cancel-item-btn {
                    padding:6px 14px; background:transparent;
                    color:#c0392b; border:1px solid #c0392b;
                    border-radius:6px; font-size:12px; font-weight:500;
                    cursor:pointer; transition:all 0.2s;
                    white-space:nowrap; flex-shrink:0;
                }
                .cancel-item-btn:hover { background:#c0392b; color:#fff; }

                /* Order Footer */
                .order-card-footer {
                    display:flex; justify-content:space-between;
                    align-items:center; flex-wrap:wrap; gap:10px;
                    padding-top:12px; border-top:1px solid #f0f0f0;
                    margin-top:4px;
                }
                .order-total { font-size:14px; color:#333; margin:0; }

                /* View Button */
                .view-btn {
                    padding:7px 18px; background:transparent;
                    color:#c9a227; border:1px solid #c9a227;
                    border-radius:6px; font-size:13px;
                    cursor:pointer; transition:all 0.2s;
                }
                .view-btn:hover { background:#c9a227; color:#fff; }

                /* Cancel Entire Order Button */
                .cancel-order-btn {
                    padding:7px 18px; background:transparent;
                    color:#c0392b; border:1px solid #c0392b;
                    border-radius:6px; font-size:13px;
                    cursor:pointer; transition:all 0.2s;
                }
                .cancel-order-btn:hover { background:#c0392b; color:#fff; }

                /* Empty / Loader */
                .empty-state { text-align:center; padding:60px 20px; color:#888; font-size:15px; }
                .empty-state-icon { font-size:52px; margin-bottom:14px; }
                .loader-wrap { text-align:center; padding:40px 20px; color:#555; }

                /* Responsive */
                @media (max-width: 768px) {
                    .acct-mobile-only-wrapper { display: block; }
                    .dashboard-wrapper { display: none !important; }
                    .wl-mob-content-panel { display: block; }
                    .order-card-footer { flex-direction: column; align-items: flex-start; }
                    .order-card-footer > div { width: 100%; }
                    .view-btn, .cancel-order-btn { flex: 1; text-align: center; }
                    .cancel-item-btn { padding: 5px 10px; font-size: 11px; }
                }

                @media (min-width: 769px) {
                    .wl-mob-content-panel { display: none !important; }
                    .dashboard-wrapper { display: flex !important; }
                }
            `}</style>

            <main className="main__content_wrapper">

                {/* MOBILE SIDEBAR */}
                <div className="acct-mobile-only-wrapper">
                    <AccountSidebar
                        mobileView={mobileView}
                        setMobileView={setMobileView}
                        activeMobilePath="/my-orders"
                        contentViewName="content"
                    />
                </div>

                {/* MOBILE CONTENT */}
                <div
                    className="wl-mob-content-panel"
                    style={{ display: mobileView === "content" ? "block" : "none" }}
                >
                    <div className="wl-mob-header">
                        <button
                            className="wl-mob-back-btn"
                            onClick={() => setMobileView("menu")}
                        >
                            ‹
                        </button>
                        <h2 className="wl-mob-title">My Orders</h2>
                        <div style={{ width: 36 }} />
                    </div>
                    <div className="wl-mob-body">
                        <OrderContent />
                    </div>
                </div>

                {/* DESKTOP LAYOUT */}
                <div className="container-fluid">
                    <div className="dashboard-wrapper">
                        <AccountSidebar
                            mobileView={mobileView}
                            setMobileView={setMobileView}
                            activeMobilePath="/my-orders"
                            contentViewName="content"
                        />
                        <div className="dashboard-content">
                            <OrderContent />
                        </div>
                    </div>
                </div>

            </main>

            <Footer />

            {/* Scroll to Top */}
            <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                style={{
                    position: 'fixed', bottom: '30px', right: '30px',
                    width: '46px', height: '46px', borderRadius: '50%',
                    backgroundColor: '#ceb256', color: '#fff', border: 'none',
                    zIndex: 1000, cursor: 'pointer',
                    display: isVisible ? 'flex' : 'none',
                    alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                }}
            >
                ↑
            </button>
        </>
    );
};

export default MyOrder;